import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import dbService from '../../../services/dbService';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
import { ArrowLeft, Paperclip, ChatLeftText, JournalText,Diagram3, CashCoin } from 'react-bootstrap-icons';
import { Tabs, Tab, Button, Form, Dropdown,Card, Spinner} from 'react-bootstrap';
import { usePermissions } from '../../../hooks/usePermissions'; // <-- IMPORT THE NEW HOOK
import PageContainer from '../../layout/PageContainer';
// import { Card } from 'react-bootstrap'; // Add Card

// Sub-component for Link Analysis
const LinkAnalysisView = ({ transactions }) => {
    if (!transactions || transactions.length === 0) return <p className="text-muted">No transaction data for link analysis.</p>;
    const centralNode = transactions[0].customerName;
    const counterparties = [...new Set(transactions.map(t => t.counterpartyCountry))];

    return (
        <div className="text-center p-4">
            <h5>Visual Relationship Map</h5>
            <div className="d-flex justify-content-center align-items-center" style={{minHeight: '200px'}}>
                <div>
                    {counterparties.map(c => <div key={c} className="border p-2 m-3 d-inline-block">{c}</div>)}
                </div>
                <div className="mx-4"><strong>---&gt;</strong></div>
                <div className="border border-primary border-3 rounded-circle p-4 d-inline-block bg-primary text-white">
                    <strong>{centralNode}</strong>
                </div>
                 <div className="mx-4"><strong>---&gt;</strong></div>
                 <div>
                    <div className="border p-2 m-3 d-inline-block">Domestic Bank</div>
                </div>
            </div>
        </div>
    );
};

// Sub-component for Transactions
const TransactionView = ({ transactions }) => {
     if (!transactions || transactions.length === 0) return <p className="text-muted">No transactions linked to this case.</p>;
     return (
        <table className="table">
            <thead><tr><th>Date</th><th>Type</th><th>Amount</th><th>Counterparty Country</th></tr></thead>
            <tbody>
                {transactions.map((t, i) => (
                    <tr key={i}>
                        <td>{t.date}</td>
                        <td>{t.type}</td>
                        <td>${t.amount.toLocaleString()}</td>
                        <td>{t.counterpartyCountry}</td>
                    </tr>
                ))}
            </tbody>
        </table>
     );
};

const CaseDetail = () => {
    const { user } = useAuth();
    const { permissions, isLoading: isPermissionsLoading } = usePermissions();

    const { caseId } = useParams();
    const [caseData, setCaseData] = useState(null);
    const [note, setNote] = useState('');
    const [users, setUsers] = useState([]);
    const [teams, setTeams] = useState([]);

    const fetchCaseAndUsers = async () => {
      const numericCaseId = parseInt(caseId, 10);
      const caseData = await dbService.getById('cases', numericCaseId);
      const userData = await dbService.getAll('users');
      setCaseData(caseData);
      setUsers(userData.filter(u => u.status === 'Active'));
      const uniqueTeams = [...new Set(userData.map(u => u.team))];
      setTeams(uniqueTeams);
    };

    useEffect(() => { fetchCaseAndUsers(); }, [caseId]);

    const updateCase = async (updatedCaseData) => {
        await dbService.update('cases', updatedCaseData);
        fetchCaseAndUsers();
    };
    console.log("permissions.canAssignCases---",permissions.canAssignCases);

    const handleAssignment = async (assignee) => {
        const updatedCase = { ...caseData, assignedTo: assignee };
        updateCase(updatedCase);
        dbService.addLog({ user: user.name, action: 'Case Assigned', details: `Case ${caseId} assigned to ${assignee}` });
        toast.success(`Case assigned to ${assignee}.`);
    };

    const handleStatusChange = async (newStatus) => {
        const isReopening = caseData.status === 'Closed' && newStatus !== 'Closed';
        let newLogAction = `Status changed to ${newStatus}`;
        if (isReopening) newLogAction = `Case Re-opened to status ${newStatus}`;

        const newLog = { user: user.name, action: newLogAction, timestamp: new Date().toISOString() };
        const updatedCase = { ...caseData, status: newStatus, auditTrail: [newLog, ...(caseData.auditTrail || [])] };
        await updateCase(updatedCase);
        await dbService.addLog({ user: user.name, action: 'Case Status Changed', details: `Case ${caseId} to ${newStatus}` });
        toast.info(`Case status updated to ${newStatus}`);
    };

    const handleAddNote = async () => {
        if (!note.trim()) { toast.error('Note cannot be empty.'); return; }
        const newNote = { user: user.name, text: note, timestamp: new Date().toISOString() };
        const updatedCase = { ...caseData, notes: [newNote, ...(caseData.notes || [])] };
        await updateCase(updatedCase);
        await dbService.addLog({ user: user.name, action: 'Note Added to Case', details: `Case ID: ${caseId}` });
        toast.success('Note added successfully');
        setNote('');
    };
    
    const exportTransactionsToCSV = () => {
        if (!caseData.transactions || caseData.transactions.length === 0) {
            toast.error("No transactions to export.");
            return;
        }
        const headers = "Date,Type,Amount,CounterpartyCountry\n";
        const rows = caseData.transactions.map(t => `${t.date},${t.type},${t.amount},${t.counterpartyCountry}`).join('\n');
        const csvContent = "data:text/csv;charset=utf-8," + headers + rows;
        
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `case_${caseData.id}_transactions.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        dbService.addLog({ user: user.name, action: 'Exported Data', details: `Exported transactions for Case ${caseId}` });
    };

     const handleInvestigationOutcome = async (isTruePositive) => {
        const updatedCase = { ...caseData, isTruePositive: isTruePositive, status: 'Closed' };
        updateCase(updatedCase);
        const outcome = isTruePositive ? 'True Positive' : 'False Positive';
        dbService.addLog({ user: user.name, action: 'Case Outcome Marked', details: `Case ${caseId} marked as ${outcome}` });
        toast.success(`Case closed and marked as ${outcome}. This will be used to retrain the ML model.`);
    };

  if (!caseData || isPermissionsLoading) {
      return <div className="text-center p-5"><Spinner animation="border" /></div>;
  }

  return (
    <PageContainer>
        <Link to="/case-management" className="btn btn-dark mb-3"><ArrowLeft className="me-2" />Back to Case List</Link>
        <div className="card">
          <div className="card-header d-flex flex-wrap justify-content-between align-items-center">
            <h3 className="mb-0">Case: {caseData.id} - {caseData.customerName}</h3>
            <div className="btn-group mt-2 mt-md-0" role="group">
              {caseData.status === 'Closed' ? (
                  <Button variant="outline-secondary" size="sm" onClick={() => handleStatusChange('Open')}>Re-open Case</Button>
              ) : (
                  <>
                      <Button variant="outline-warning" size="sm" onClick={() => handleStatusChange('In Review')}>Set to Review</Button>
                      <Button variant="outline-info" size="sm" onClick={() => handleStatusChange('Escalated')}>Escalate</Button>
                      <Button variant="outline-success" size="sm" onClick={() => handleStatusChange('Closed')}>Close Case</Button>
                  </>
              )}
            </div>
          </div>
          <div className="card-body">
            <Tabs defaultActiveKey="summary" id="case-details-tabs" className="mb-3">
              <Tab eventKey="summary" title="Summary">
                <div className="p-3">
                  <h4>Case Summary</h4>
                  <p><strong>Customer ID:</strong> <Link to={`/kyc-profile/${caseData.customerId}`}>{caseData.customerId}</Link></p>
                  <p><strong>Risk Level:</strong> {caseData.risk}</p>
                  <p><strong>Current Status:</strong> {caseData.status}</p>
                  <p><strong>Date Opened:</strong> {caseData.openDate}</p>
                  <p><strong>Assigned To:</strong>
                      <Dropdown className="d-inline ms-2">
                          <Dropdown.Toggle variant="outline-secondary" size="sm" id="dropdown-basic">
                              {caseData.assignedTo}
                          </Dropdown.Toggle>
                          {permissions.canAssignCases ? (
                              <Dropdown.Menu>
                                  <Dropdown.Header>Assign to Team</Dropdown.Header>
                                  {teams.map(t => <Dropdown.Item key={t} onClick={() => handleAssignment(t)}>{t}</Dropdown.Item>)}
                                  <Dropdown.Divider />
                                  <Dropdown.Header>Assign to User</Dropdown.Header>
                                  {users.map(u => <Dropdown.Item key={u.id} onClick={() => handleAssignment(u.name)}>{u.name}</Dropdown.Item>)}
                              </Dropdown.Menu>
                          ) : (
                              // Render a disabled menu for a better UX than just a disabled button
                              <Dropdown.Menu>
                                  <Dropdown.Item disabled>Permission Denied</Dropdown.Item>
                              </Dropdown.Menu>
                          )}
                      </Dropdown>
                  </p>
                </div>
              </Tab>
              <Tab eventKey="transactions" title={<span><CashCoin className="me-2"/>Transactions</span>}>
                  <div className="p-3">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                          <h4>Underlying Transactions</h4>
                          <Button variant="success" size="sm" onClick={exportTransactionsToCSV}>Export to CSV</Button>
                      </div>
                      <TransactionView transactions={caseData.transactions} />
                  </div>
              </Tab>
              <Tab eventKey="link-analysis" title={<span><Diagram3 className="me-2"/>Link Analysis</span>}>
                  <div className="p-3"><LinkAnalysisView transactions={caseData.transactions} /></div>
              </Tab>
              <Tab eventKey="notes" title={<span><ChatLeftText className="me-2" />Notes</span>}>
                <div className="p-3">
                  <h4>Case Notes</h4>
                  <div className="mb-3">
                    <Form.Control as="textarea" rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="Add a new note..." />
                    <Button variant="primary" size="sm" className="mt-2" onClick={handleAddNote}>Add Note</Button>
                  </div>
                  <ul className="list-group">
                    {(caseData.notes && caseData.notes.length > 0) ? [...caseData.notes].reverse().map((n, i) => (
                      <li key={i} className="list-group-item">
                        <p className="mb-1">{n.text}</p>
                        <small className="text-muted">by {n.user} on {new Date(n.timestamp).toLocaleString()}</small>
                      </li>
                    )) : <li className="list-group-item text-muted">No notes yet.</li>}
                  </ul>
                </div>
              </Tab>
              <Tab eventKey="documents" title={<span><Paperclip className="me-2" />Documents</span>}>
                <div className="p-3">
                  <h4>Attached Documents</h4>
                  <p className="text-muted">Simulated file upload.</p>
                  <Form.Control type="file" className="mb-3" />
                  <ul className="list-group">
                    <li className="list-group-item">passport_scan.pdf</li>
                    <li className="list-group-item">utility_bill.jpg</li>
                  </ul>
                </div>
              </Tab>
              <Tab eventKey="audit" title={<span><JournalText className="me-2" />Case Audit Trail</span>}>
                <div className="p-3">
                  <h4>Case-Specific History</h4>
                  <ul className="list-group">
                    {(caseData.auditTrail && caseData.auditTrail.length > 0) ? [...caseData.auditTrail].reverse().map((log, i) => (
                      <li key={i} className="list-group-item">
                        <p className="mb-1"><strong>Action:</strong> {log.action}</p>
                        <small className="text-muted">by {log.user} on {new Date(log.timestamp).toLocaleString()}</small>
                      </li>
                    )) : <li className="list-group-item text-muted">No history for this case yet.</li>}
                  </ul>
                </div>
              </Tab>
            </Tabs>

            {caseData.status !== 'Closed' && (
                  <Card className="mt-4">
                      <Card.Header as="h5">Investigation Outcome</Card.Header>
                      <Card.Body>
                          <Card.Text>After your investigation, mark the outcome of this case. This will close the case and provide valuable feedback for the machine learning model.</Card.Text>
                          <Button variant="success" className="me-2" onClick={() => handleInvestigationOutcome(true)}>Mark as True Positive</Button>
                          <Button variant="secondary" onClick={() => handleInvestigationOutcome(false)}>Mark as False Positive</Button>
                      </Card.Body>
                  </Card>
              )}

              {caseData.status === 'Closed' && caseData.isTruePositive !== undefined && (
                  <div className={`alert mt-4 ${caseData.isTruePositive ? 'alert-success' : 'alert-secondary'}`}>
                      <strong>Outcome Recorded:</strong> This case was closed and marked as a <strong>{caseData.isTruePositive ? 'True Positive' : 'False Positive'}</strong>.
                  </div>
              )}
          </div>
        </div>
    </PageContainer>
  );
};

export default CaseDetail;